import React from 'react';
import { CustomerOption, NewCloseData, TrialBalancePeriodRecord } from './types';
import { formatNumber } from './utils';
import { CloseContextSummary } from './CloseContextSummary';

interface Step4TrialBalanceProps {
  loadingTrialBalance: boolean;
  showTrialBalance: boolean;
  trialBalanceData: TrialBalancePeriodRecord[];
  customers: CustomerOption[];
  newCloseData: NewCloseData;
  onBack?: () => void;
  onContinue?: () => void;
  loading?: boolean;
  customerOid?: string;
  year?: string;
}

const Step4TrialBalance: React.FC<Step4TrialBalanceProps> = ({
  loadingTrialBalance,
  showTrialBalance,
  trialBalanceData,
  customers,
  newCloseData,
  onBack,
  onContinue,
  loading = false,
}) => {
  // Tax data is now merged in parent component, so we can use trialBalanceData directly
  const mergedTrialBalanceData = trialBalanceData;
  return (
    <>
      {/* Header with buttons */}
      <div className="flex items-start justify-end mb-4">
        <div className="flex gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Буцах
            </button>
          )}
          {onContinue && (
            <button
              onClick={onContinue}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              disabled={loading || loadingTrialBalance}
            >
              {loading || loadingTrialBalance ? 'Ажиллаж байна...' : 'Үргэлжлүүлэх'}
            </button>
          )}
        </div>
      </div>

      <CloseContextSummary customers={customers} newCloseData={newCloseData} />

      {/* Trial Balance Section */}
      <div>
        {loadingTrialBalance ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-3 text-sm text-gray-600">
              Trial Balance болон Татварын мэдээлэл уншиж байна...
            </p>
          </div>
        ) : (
          <>
            {/* TrialBalancePeriod Detailed Rows */}
            {showTrialBalance && mergedTrialBalanceData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-300">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 border border-gray-300">Дансны код</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 border border-gray-300">Дансны нэр</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 border border-gray-300">Ангилал</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Дебет C1</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Кредит C1</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Дебет</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Кредит</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Дебет C2</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Кредит C2</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Дебет Тат C1</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Кредит Тат C1</th> 
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Дебет Тат C2</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 border border-gray-300">Кредит Тат C2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergedTrialBalanceData.map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap text-gray-900 border border-gray-300">{row.AccountCode}</td>
                        <td className="px-3 py-2 text-gray-900 border border-gray-300">{row.AccountName}</td>
                        <td className="px-3 py-2 text-gray-600 border border-gray-300">{row.AccountCategoryName}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.DebitAmountC1)}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.CreditAmountC1)}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.DebitAmount)}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.CreditAmount)}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.DebitAmountC2)}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.CreditAmountC2)}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.TaxCreditC1)}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.TaxDebitC1)}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.TaxCreditC2)}</td>
                        <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.TaxDebitC2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr>
                      <td colSpan={3} className="px-3 py-2 font-semibold text-gray-900 border border-gray-300">Нийт:</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.DebitAmountC1 || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.CreditAmountC1 || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.DebitAmount || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.CreditAmount || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.DebitAmountC2 || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.CreditAmountC2 || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.TaxCreditC1 || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.TaxDebitC1 || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.TaxCreditC2 || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                        {formatNumber(mergedTrialBalanceData.reduce((sum, row) => sum + (row.TaxDebitC2 || 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : !loadingTrialBalance && (
              <div className="text-center py-8 text-gray-500">
                Trial Balance мэдээлэл олдсонгүй
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Step4TrialBalance;
